import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { asset } from "../data/portfolio";

const REEL = "showreel/showreel-2026.mp4";
const POSTER = "showreel/poster-mid.jpg";

export function useReel() {
  const [params, setParams] = useSearchParams();
  const open = params.get("reel") === "1";
  const openReel = () => { const p = new URLSearchParams(params); p.set("reel", "1"); setParams(p); };
  const closeReel = () => { const p = new URLSearchParams(params); p.delete("reel"); setParams(p, { replace: true }); };
  return { open, openReel, closeReel };
}

export default function ShowreelOverlay() {
  const { open, closeReel } = useReel();
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const vref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeReel();
      if (e.key === " ") { e.preventDefault(); toggle(); }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const v = vref.current;
    if (v) { v.currentTime = 0; v.play?.().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    // eslint-disable-next-line
  }, [open]);

  const toggle = () => {
    const v = vref.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  if (!open) return null;
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return createPortal(
    <div className="fixed inset-0 z-[400] flex flex-col" style={{ background: "#060608", height: "100dvh" }} data-testid="showreel-overlay">
      <div className="flex items-center justify-between page-x py-4" style={{ color: "#fff" }}>
        <span className="mono" style={{ color: "var(--lime)" }}>vdesigns.eps</span>
        <span className="mono hidden md:inline">SHOWREEL_2026.MP4 — {playing ? "PLAYING" : "PAUSED"}</span>
        <button className="mono" style={{ color: "var(--lime)" }} onClick={closeReel} data-testid="showreel-close">CLOSE ✕</button>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center px-4" onClick={toggle}>
        <div style={{ width: "100%", maxWidth: 1180, aspectRatio: "16 / 9" }}>
          <video
            ref={vref}
            src={asset(REEL)}
            poster={asset(POSTER)}
            loop
            playsInline
            muted={muted}
            onTimeUpdate={(e) => setT(e.target.currentTime)}
            onLoadedMetadata={(e) => setDur(e.target.duration)}
            style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
          />
        </div>
      </div>

      <div className="page-x pb-6" style={{ color: "#fff" }}>
        <div className="flex items-center gap-4">
          <button onClick={toggle} className="mono" style={{ background: "var(--lime)", color: "#000", borderRadius: 999, height: 36, width: 36 }} data-testid="showreel-toggle">
            {playing ? "❚❚" : "▶"}
          </button>
          <span className="mono">{fmt(t)} / {fmt(dur || 0)}</span>
          <button onClick={() => setMuted((m) => !m)} className="mono" style={{ border: "1px solid rgba(255,255,255,.3)", borderRadius: 999, padding: "7px 12px", color: "#fff" }} data-testid="showreel-mute">
            {muted ? "🔇 Sound off" : "🔊 Sound on"}
          </button>
          <span className="mono" style={{ marginLeft: "auto", opacity: .7 }}>SPACE = PAUSE · ESC = CLOSE</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
